/* Atlas v3 — CandleChart: the ticker price chart (mobile TickerSheet + the
   V3 desktop TickerPanel right dock). TradingView lightweight-charts v5.

   Replaced the hand-rolled SVG chart (2026-08-28). That one was a faithful
   port of the locked v3 mockup's SVG, which meant zoom/pan and indicator
   sub-panes did not exist and would have had to be hand-built; both are
   configuration here. Everything the SVG drew is preserved: gridlines +
   price axis, gradient area in line mode, candle bodies/wicks, the volume
   row, the last-price tag, and a crosshair with a date pill.

   Props
     bars        [{ t:ms, o,h,l,c,v }] straight off /api/chart-bars
     type        'line' | 'area' | 'candles' | 'candle' | 'hollow' | 'heikin'
                 ('candle' singular is the desktop panel's spelling; 'line'
                 renders as an AREA with a gradient, matching the old SVG —
                 a plain line reads noticeably thinner/worse.)
     up          direction tint for line/area mode
     height      px
     tf          timeframe chip, drives intraday axis formatting
     indicators  see DEFAULT_INDICATORS
     warmup      OPTIONAL longer bar series (same instrument, same interval)
                 used ONLY to give indicators enough history. Every series is
                 drawn from it and the view is then clamped to `bars`'s window,
                 which is how a 1M chart can show MACD at all: 1M is ~25 daily
                 bars and MACD needs slow(26)+signal(9) ≈ 35, so without lead-in
                 the line simply doesn't exist. Same trick real charting apps
                 use — compute on more data than you display.
     zoom        grant the mouse wheel (expanded view only — inline, the wheel
                 is how the surrounding panel scrolls). Pinch and drag are
                 always on; see the handleScroll block.
     scaleMode   'normal' | 'log' | 'percent' — the price axis. Log matters on
                 the long ranges (MU's MAX is +6,900%, which on a linear scale
                 flattens a decade into a line along the bottom).

   Two affordances lifted from TradingView, both native API here:
     • a crosshair LEGEND (O/H/L/C + every enabled indicator's value at the
       hovered bar). Without it an enabled RSI shows its shape but never its
       number, which is most of the value.
     • a GO LIVE button that appears only once you've panned off the right
       edge, and snaps back via timeScale().scrollToRealTime().

   Indicator colours follow the v3 rule: amber is the only non-semantic
   accent (weight/dash distinguishes the lines), green/red are reserved for
   direction. No decorative palette. */
import { useEffect, useRef, useState } from 'react'
import {
  createChart, CandlestickSeries, LineSeries, AreaSeries, HistogramSeries,
  LineStyle, CrosshairMode, PriceScaleMode,
} from 'lightweight-charts'
import { sma, ema, rsi, macd, bollinger, vwap, heikinAshi, toLine } from './indicators'

export const DEFAULT_INDICATORS = {
  volume: true,
  sma20: false, sma50: false, sma200: false,
  ema20: false, ema50: false,
  bb: false, vwap: false,
  rsi: false, macd: false,
}

// Resolve a --studio-* HSL triplet against the chart element, which inherits
// [data-theme] — so the Night palette works without a rebuild.
function tk(el, name, alpha) {
  const v = getComputedStyle(el).getPropertyValue(name).trim()
  if (!v) return alpha != null ? 'rgba(0,0,0,0)' : '#888'
  return alpha != null ? `hsl(${v} / ${alpha})` : `hsl(${v})`
}

// Bars → the lib's row shape. Mirrors the old SVG chart's tolerance: drop
// null closes, synthesise o/h/l from the previous close when the endpoint
// returns a close-only series. De-dupe + sort: the lib throws on repeated or
// unordered timestamps.
function normalise(bars) {
  const clean = (Array.isArray(bars) ? bars : []).filter((b) => b && b.c != null)
  if (clean.length === 0) return []
  const seen = new Set()
  const rows = []
  let prev = clean[0].c
  for (const b of clean) {
    const time = Math.floor(b.t / 1000)
    if (!Number.isFinite(time) || seen.has(time)) continue
    seen.add(time)
    const close = b.c
    const open = b.o ?? prev
    prev = close
    rows.push({
      time,
      open,
      close,
      high: b.h ?? Math.max(open, close),
      low: b.l ?? Math.min(open, close),
      volume: b.v ?? 0,
    })
  }
  rows.sort((a, b) => a.time - b.time)
  return rows
}

export function CandleChart({
  bars, warmup = null, type = 'candles', up = true, height = 224, tf = '1D',
  indicators = DEFAULT_INDICATORS, zoom = false, scaleMode = 'normal', measure = false, onMeasureEnd,
}) {
  const wrapRef = useRef(null)
  const chartRef = useRef(null)
  const [legend, setLegend] = useState(null)   // values at the hovered bar
  const [offLive, setOffLive] = useState(false) // panned away from the right edge
  const [span, setSpan] = useState(null)        // measure-tool result
  const anchorRef = useRef(null)                // first tapped point while measuring
  // Snapshot so the effect re-runs on a real toggle change, not on every
  // parent render handing us a fresh object literal.
  const indKey = JSON.stringify(indicators)

  // Pane geometry is computed HERE, not in the effect, because the wrapper
  // must own the total height: the chart is created with autoSize and fills
  // whatever box this div is. Passing an explicit `width` instead let the
  // chart keep a width measured before layout settled, which rendered the
  // plot ~66px (one price-axis) wider than its container and pushed the axis
  // off the right edge of the advanced view.
  const ind0 = { ...DEFAULT_INDICATORS, ...indicators }
  const volH = ind0.volume ? Math.max(38, Math.round(height * 0.22)) : 0
  const subPanes = (ind0.rsi ? 1 : 0) + (ind0.macd ? 1 : 0)
  // An oscillator needs real height to read as anything but a squiggle;
  // measured at 28px it was illegible.
  const subH = subPanes ? Math.max(64, Math.min(96, Math.round(height * 0.3))) : 0
  const totalH = height + volH + subPanes * subH

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const view = normalise(bars)
    if (view.length === 0) return
    // Draw from the warm-up series when it genuinely extends the history, then
    // clamp the visible window back to `bars`. Indicators get their lead-in;
    // the user still sees the timeframe they picked.
    const lead = normalise(warmup)
    const usingLead = lead.length > view.length
    const rows = usingLead ? lead : view

    const ind = { ...DEFAULT_INDICATORS, ...indicators }
    const intraday = tf === '1D' || tf === '5D'
    const upc = tk(el, '--studio-success')
    const dnc = tk(el, '--studio-danger')
    const accent = tk(el, '--studio-accent')
    const subtle = tk(el, '--studio-text-subtle')
    const dir = up ? upc : dnc

    const chart = createChart(el, {
      // Sizes to this element (ResizeObserver-backed inside the lib). Passing
      // an explicit width instead let the chart keep a width measured before
      // layout settled, rendering the plot one price-axis wider than its
      // container and pushing the axis off the right edge of the advanced view.
      autoSize: true,
      layout: {
        background: { type: 'solid', color: 'transparent' },
        textColor: subtle,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9.5,
        attributionLogo: false,
        panes: { separatorColor: tk(el, '--studio-border', 0.55), separatorHoverColor: tk(el, '--studio-accent', 0.3), enableResize: zoom },
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: tk(el, '--studio-text', 0.05) },
      },
      leftPriceScale: { visible: false, borderVisible: false },
      rightPriceScale: {
        visible: true,
        borderVisible: false,
        mode: scaleMode === 'log' ? PriceScaleMode.Logarithmic
          : scaleMode === 'percent' ? PriceScaleMode.Percentage
          : PriceScaleMode.Normal,
        scaleMargins: { top: 0.14, bottom: 0.1 },
      },
      timeScale: {
        visible: true, borderVisible: false, timeVisible: intraday, secondsVisible: false,
        // Pinned: a drag can't strand the user on blank canvas past the
        // series. Switching timeframe remounts and refits, which is the
        // other way back out of a zoom.
        fixLeftEdge: true, fixRightEdge: true,
        tickMarkFormatter: (t, mark) => {
          const d = new Date(t * 1000)
          if (mark === 0) return String(d.getFullYear())
          if (mark === 1) return d.toLocaleDateString('en-US', { month: 'short' })
          if (mark === 2) return String(d.getDate())
          return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        },
      },
      localization: {
        // No chart-level priceFormatter: it overrides EVERY series' own
        // priceFormat, which made the volume pane's axis print a raw
        // "10000000.00" instead of the abbreviated volume scale.
        timeFormatter: (t) => {
          const d = new Date(t * 1000)
          return intraday
            ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
            : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: tk(el, '--studio-accent-soft', 0.5), width: 1, style: LineStyle.Dashed, labelVisible: true, labelBackgroundColor: accent },
        horzLine: { color: tk(el, '--studio-accent-soft', 0.5), width: 1, style: LineStyle.Dashed, labelVisible: true, labelBackgroundColor: accent },
      },
      // Zoom/pan is on everywhere, including the inline chart inside the
      // TickerSheet. The thing that would have made that unusable is
      // vertTouchDrag: a vertical swipe starting on the chart would pan the
      // chart instead of scrolling the sheet. Turning just that off lets the
      // chart own horizontal drags and pinch while vertical swipes fall
      // through to the page — so no gesture conflict, and no need to open a
      // separate view just to zoom.
      // Pinch-zoom and horizontal drag are ON everywhere, inline chart
      // included. Two gestures are deliberately NOT taken, because each one is
      // how the user scrolls the surface the chart sits in:
      //   vertTouchDrag — a vertical swipe starting on the chart must scroll
      //     the TickerSheet, not pan the chart (verified: the sheet scrolls).
      //   mouseWheel — on desktop the wheel scrolls the right dock, so it is
      //     granted only to the expanded view (`zoom`), where the chart owns
      //     the surface.
      handleScroll: measure ? false : {
        mouseWheel: zoom,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: measure ? false : {
        mouseWheel: zoom,
        pinch: true,
        axisPressedMouseMove: true,
        axisDoubleClickReset: true,   // double-tap an axis to snap back
      },
      kineticScroll: { mouse: false, touch: true },
    })

    const priceLine = { priceLineVisible: true, priceLineColor: tk(el, '--studio-accent', 0.6), priceLineStyle: LineStyle.Dashed }

    // ── price series ─────────────────────────────────────────────
    const kind = type === 'candle' ? 'candles' : type
    if (kind === 'candles' || kind === 'hollow' || kind === 'heikin') {
      const src = kind === 'heikin' ? heikinAshi(rows) : rows
      const hollow = kind === 'hollow'
      const s = chart.addSeries(CandlestickSeries, {
        upColor: hollow ? 'rgba(0,0,0,0)' : upc,
        downColor: dnc,
        borderUpColor: upc, borderDownColor: dnc,
        wickUpColor: upc, wickDownColor: dnc,
        ...priceLine,
      })
      s.setData(src)
    } else if (kind === 'plainline') {
      const s = chart.addSeries(LineSeries, { color: dir, lineWidth: 2, ...priceLine })
      s.setData(rows.map((r) => ({ time: r.time, value: r.close })))
    } else {
      // 'line' and 'area' both render as the gradient area the SVG chart drew.
      const s = chart.addSeries(AreaSeries, {
        lineColor: dir,
        topColor: up ? tk(el, '--studio-success', 0.26) : tk(el, '--studio-danger', 0.26),
        bottomColor: up ? tk(el, '--studio-success', 0) : tk(el, '--studio-danger', 0),
        lineWidth: 2,
        ...priceLine,
      })
      s.setData(rows.map((r) => ({ time: r.time, value: r.close })))
    }

    // ── overlays on the price pane ───────────────────────────────
    // Amber-family only, separated by weight and dash (v3: amber is a line,
    // never a fill; no decorative colour set).
    const overlay = (vals, color, width, style) => {
      const pts = toLine(rows, vals)
      if (pts.length === 0) return
      const s = chart.addSeries(LineSeries, {
        color, lineWidth: width, lineStyle: style,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
      })
      s.setData(pts)
    }
    if (ind.sma20) overlay(sma(rows, 20), accent, 1, LineStyle.Solid)
    if (ind.sma50) overlay(sma(rows, 50), tk(el, '--studio-accent', 0.75), 1, LineStyle.Dashed)
    if (ind.sma200) overlay(sma(rows, 200), tk(el, '--studio-text-subtle', 0.9), 1, LineStyle.Solid)
    if (ind.ema20) overlay(ema(rows, 20), tk(el, '--studio-accent-soft', 0.95), 1, LineStyle.Dotted)
    if (ind.ema50) overlay(ema(rows, 50), tk(el, '--studio-text', 0.55), 1, LineStyle.Dashed)
    if (ind.vwap) overlay(vwap(rows), tk(el, '--studio-accent', 0.85), 1, LineStyle.LargeDashed)
    if (ind.bb) {
      const b = bollinger(rows, 20, 2)
      overlay(b.map((x) => x && x.upper), tk(el, '--studio-text-subtle', 0.7), 1, LineStyle.Solid)
      overlay(b.map((x) => x && x.lower), tk(el, '--studio-text-subtle', 0.7), 1, LineStyle.Solid)
      overlay(b.map((x) => x && x.mid), tk(el, '--studio-text-subtle', 0.45), 1, LineStyle.Dotted)
    }

    // ── panes ────────────────────────────────────────────────────
    // Pane sizing uses setStretchFactor, NOT setHeight. setHeight
    // redistributes across the panes that already exist, so each call clawed
    // space back off the previous ones and volume/RSI landed at 28px against
    // the 45/58 asked for (measured) — an oscillator in 28px is a squiggle.
    // Stretch factors are proportional and order-independent, which is what
    // "price gets most of it, each sub-pane gets a readable slice" actually
    // wants. Weights below are relative, applied once after all panes exist.
    const paneWeights = []
    let pane = 1
    if (ind.volume) {
      // Volume rides its own scale with the axis hidden. Its labels ("10M"/"0")
      // sat directly against the next pane's top label and the two collided;
      // the old SVG chart drew no volume axis either, and the magnitude is
      // legible from the bars.
      const vol = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' }, priceScaleId: 'vol',
        lastValueVisible: false, priceLineVisible: false,
      }, pane)
      chart.panes()[pane].priceScale('vol').applyOptions({ visible: false, scaleMargins: { top: 0.1, bottom: 0 } })
      vol.setData(rows.map((r) => ({
        time: r.time,
        value: r.volume,
        color: r.close >= r.open ? tk(el, '--studio-success', 0.38) : tk(el, '--studio-danger', 0.38),
      })))
      paneWeights.push([pane, volH])
      pane++
    }
    if (ind.rsi) {
      const pts = toLine(rows, rsi(rows, 14))
      if (pts.length) {
        // lastValueVisible off on the sub-panes: the library draws the tag
        // over the axis without suppressing the tick behind it (the old SVG
        // chart hid colliding labels by hand), so RSI's tag sat on top of its
        // own 60/80 ticks. The crosshair still reads the value out.
        const s = chart.addSeries(LineSeries, {
          color: accent, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
        }, pane)
        s.setData(pts)
        // Breathing room top and bottom, or the pane's own 80/20 axis labels
        // sit flush against the pane divider and get clipped in half.
        s.priceScale().applyOptions({ scaleMargins: { top: 0.18, bottom: 0.12 } })
        // 70 / 30 reference hairlines.
        for (const lvl of [70, 30]) {
          s.createPriceLine({ price: lvl, color: tk(el, '--studio-text-subtle', 0.45), lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false })
        }
        paneWeights.push([pane, subH])
        pane++
      }
    }
    if (ind.macd) {
      const m = macd(rows)
      const line = toLine(rows, m.map((x) => x && x.macd))
      if (line.length) {
        const h = chart.addSeries(HistogramSeries, {
          priceLineVisible: false, lastValueVisible: false,
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        }, pane)
        h.setData(m.map((x, i) => (x == null ? null : {
          time: rows[i].time, value: x.hist,
          color: x.hist >= 0 ? tk(el, '--studio-success', 0.5) : tk(el, '--studio-danger', 0.5),
        })).filter(Boolean))
        const ml = chart.addSeries(LineSeries, { color: accent, lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, pane)
        ml.setData(line)
        const sl = chart.addSeries(LineSeries, { color: tk(el, '--studio-text-subtle', 0.9), lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, pane)
        sl.setData(toLine(rows, m.map((x) => x && x.signal)))
        sl.priceScale().applyOptions({ scaleMargins: { top: 0.18, bottom: 0.14 } })
        paneWeights.push([pane, subH])
      }
    }

    // Proportional split. The px figures computed above become weights: the
    // price pane keeps `height`, each sub-pane its own slice of the total.
    const priceP = chart.panes()[0]
    if (priceP) priceP.setStretchFactor(height)
    for (const [idx, h] of paneWeights) {
      const pn = chart.panes()[idx]
      if (pn) pn.setStretchFactor(h)
    }

    chartRef.current = chart

    // Double-tap/double-click anywhere on the plot refits — the axis already
    // had axisDoubleClickReset, but the axis is a thin target on a phone and
    // the plot is the thing your thumb is already on.
    const ts0 = chart.timeScale()
    const onDbl = () => {
      if (usingLead) ts0.setVisibleRange({ from: view[0].time, to: view[view.length - 1].time })
      else ts0.fitContent()
    }
    el.addEventListener('dblclick', onDbl)

    // ── crosshair legend ─────────────────────────────────────────
    // Values at the hovered bar. Reading them off `rows` by timestamp rather
    // than out of the event keeps indicator values in step with the candle,
    // and means adding an indicator needs no extra series bookkeeping here.
    const byTime = new Map(rows.map((r, i) => [r.time, i]))
    const smaV = ind.sma20 ? sma(rows, 20) : null
    const ema20V = ind.ema20 ? ema(rows, 20) : null
    const rsiV = ind.rsi ? rsi(rows, 14) : null
    const macdV = ind.macd ? macd(rows) : null
    const vwapV = ind.vwap ? vwap(rows) : null
    // ── measure tool ─────────────────────────────────────────────
    // Two taps rather than a drag: a drag is how the chart pans, and stealing
    // it would make measuring feel like a broken pan. Pan/zoom are suspended
    // above while `measure` is on, so a tap can't be misread.
    const onMeasureClick = (param) => {
      if (!measure || !param || param.time == null) return
      const i = byTime.get(param.time)
      if (i == null) return
      const pt = { i, time: param.time, price: rows[i].close }
      if (!anchorRef.current) { anchorRef.current = pt; setSpan({ from: pt, to: null }); return }
      const a = anchorRef.current
      const from = a.i <= pt.i ? a : pt
      const to = a.i <= pt.i ? pt : a
      setSpan({
        from, to,
        abs: to.price - from.price,
        pct: from.price ? ((to.price - from.price) / from.price) * 100 : null,
        bars: Math.abs(to.i - from.i),
      })
      anchorRef.current = null
      if (onMeasureEnd) onMeasureEnd()
    }
    chart.subscribeClick(onMeasureClick)

    const onCross = (param) => {
      const i = param && param.time != null ? byTime.get(param.time) : undefined
      if (i == null) { setLegend(null); return }
      const r = rows[i]
      setLegend({
        o: r.open, h: r.high, l: r.low, c: r.close, v: r.volume, t: r.time,
        sma20: smaV && smaV[i], ema20: ema20V && ema20V[i], vwap: vwapV && vwapV[i],
        rsi: rsiV && rsiV[i], macd: macdV && macdV[i] && macdV[i].macd,
      })
    }
    chart.subscribeCrosshairMove(onCross)

    // ── "go live" affordance ─────────────────────────────────────
    // Only meaningful once zoom/pan exists: show the button when the right
    // edge of the data has been scrolled out of view. TradingView hides it
    // at the live edge for the same reason — it'd be a no-op button.
    const ts = chart.timeScale()
    const onRange = () => {
      const r = ts.getVisibleLogicalRange()
      // scrollPosition() > ~1 bar past the last one means we're off the edge.
      setOffLive(!!r && r.to < rows.length - 1.5)
    }
    ts.subscribeVisibleLogicalRangeChange(onRange)

    if (usingLead) {
      // Clamp to the selected timeframe. Panning left into the lead-in is fine
      // (and useful) — it's real data, just outside the chosen window.
      chart.timeScale().setVisibleRange({ from: view[0].time, to: view[view.length - 1].time })
    } else {
      chart.timeScale().fitContent()
    }

    return () => {
      el.removeEventListener('dblclick', onDbl)
      chart.unsubscribeClick(onMeasureClick)
      chart.unsubscribeCrosshairMove(onCross)
      ts.unsubscribeVisibleLogicalRangeChange(onRange)
      chartRef.current = null
      chart.remove()
    }
  }, [bars, warmup, type, up, height, tf, zoom, scaleMode, measure, onMeasureEnd, indKey, indicators, volH, subH, subPanes])

  // Leaving measure mode drops any half-finished span. Must live above the
  // empty-data early return — hooks can't be called conditionally.
  useEffect(() => { if (!measure) { anchorRef.current = null; setSpan(null) } }, [measure])

  const rows = normalise(bars)
  if (rows.length === 0) {
    return (
      <div style={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--studio-font-mono)', fontSize: 10.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>
        NO CHART DATA
      </div>
    )
  }
  const fmt = (n, d = 2) => (n == null || !Number.isFinite(n) ? '—' : n.toFixed(d))
  const fmtVol = (n) => (n == null ? '—' : n >= 1e9 ? `${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : String(n))
  const L = legend
  // Direction of the hovered bar drives the O/H/L/C colour, same semantic as
  // the candles themselves.
  const lc = L && L.c >= L.o ? '--studio-success' : '--studio-danger'

  return (
    <div style={{ width: '100%', height: totalH, position: 'relative' }}>
      <div ref={wrapRef} style={{ width: '100%', height: '100%', overflow: 'hidden' }} />

      {/* Crosshair legend. Absolutely positioned over the plot and
          pointer-events:none so it can never eat a drag or a pinch. */}
      {L && (
        <div style={{
          position: 'absolute', top: 4, left: 6, zIndex: 3, pointerEvents: 'none',
          display: 'flex', flexWrap: 'wrap', gap: '2px 9px',
          // Leave room for the measure badge (top-right) so the two never
          // overprint; the badge is ~150px with a result in it.
          maxWidth: measure ? 'calc(100% - 170px)' : 'calc(100% - 70px)',
          fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.02em',
          color: 'hsl(var(--studio-text-subtle))',
        }}>
          {[['O', fmt(L.o)], ['H', fmt(L.h)], ['L', fmt(L.l)], ['C', fmt(L.c)]].map(([k, v]) => (
            <span key={k}>{k} <span style={{ color: `hsl(var(${lc}))` }}>{v}</span></span>
          ))}
          {ind0.volume && <span>V <span style={{ color: 'hsl(var(--studio-text-muted))' }}>{fmtVol(L.v)}</span></span>}
          {ind0.sma20 && <span>SMA20 <span style={{ color: 'hsl(var(--studio-accent))' }}>{fmt(L.sma20)}</span></span>}
          {ind0.ema20 && <span>EMA20 <span style={{ color: 'hsl(var(--studio-accent-soft))' }}>{fmt(L.ema20)}</span></span>}
          {ind0.vwap && <span>VWAP <span style={{ color: 'hsl(var(--studio-accent))' }}>{fmt(L.vwap)}</span></span>}
          {ind0.rsi && <span>RSI <span style={{ color: 'hsl(var(--studio-accent))' }}>{fmt(L.rsi, 1)}</span></span>}
          {ind0.macd && <span>MACD <span style={{ color: 'hsl(var(--studio-accent))' }}>{fmt(L.macd)}</span></span>}
        </div>
      )}

      {/* Measure readout. Green/red by direction — the one semantic colour
          rule the design system allows outside price itself. */}
      {measure && (
        <div style={{
          position: 'absolute', top: 4, right: 8, zIndex: 4, pointerEvents: 'none',
          fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.04em',
          padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)',
          background: 'hsl(var(--studio-surface) / 0.92)',
          border: `1px solid hsl(var(--studio-${span && span.to ? (span.pct >= 0 ? 'success' : 'danger') : 'accent'}) / 0.55)`,
          color: span && span.to
            ? `hsl(var(--studio-${span.pct >= 0 ? 'success' : 'danger'}))`
            : 'hsl(var(--studio-accent))',
        }}>
          {span && span.to
            ? `${span.pct >= 0 ? '+' : ''}${fmt(span.pct)}%  ${span.abs >= 0 ? '+' : ''}${fmt(span.abs)}  ${span.bars} bars`
            : span ? 'TAP SECOND POINT' : 'TAP FIRST POINT'}
        </div>
      )}

      {/* Snap back to the live edge. Only rendered once panned away — at the
          right edge it would be a no-op, which is why TradingView hides it. */}
      {offLive && (
        <button
          onClick={() => chartRef.current && chartRef.current.timeScale().scrollToRealTime()}
          aria-label="Back to latest"
          title="Back to latest"
          style={{
            position: 'absolute', right: 8, bottom: 26, zIndex: 4, width: 26, height: 26,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', cursor: 'pointer',
            border: '1px solid hsl(var(--studio-border))',
            background: 'hsl(var(--studio-surface) / 0.92)',
            color: 'hsl(var(--studio-accent))',
            fontFamily: 'var(--studio-font-mono)', fontSize: 11, lineHeight: 1, padding: 0,
          }}>›|</button>
      )}
    </div>
  )
}
